import { addDoc, collection } from 'firebase/firestore';
import db from '@/firebase/firestore';
import { isMailDedupeHit, markMailDedupe } from '@/lib/suggestionsStatusSession';

const RECIPIENT = 'cto@travelin.me';
const SUBJECT_MAX = 120;
const TEXT_MAX = 4000;
const ERROR_MAX = 500;

export type StatusFailurePayload = {
  uid: string | null;
  structureId: string;
  suggestionId: string;
  newStatus: string;
  prevStatus?: string | null;
  route?: string;
  timestamp: string;
  error?: string;
  source?: string;
  env?: string;
  appVersion?: string;
  itemCount?: number;
  itemIds?: string[];
};

const truncate = (value: string, limit: number) => {
  if (!value) return '';
  return value.length > limit ? `${value.slice(0, limit - 3)}...` : value;
};

const sanitizeText = (value: string) => value.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();

const buildTextBody = (payload: StatusFailurePayload): string => {
  const lines: string[] = [];
  lines.push('suggestions_status write failed');
  lines.push(`uid: ${payload.uid ?? 'anonymous'}`);
  lines.push(`structureId: ${payload.structureId}`);
  lines.push(`suggestionId: ${payload.suggestionId}`);
  lines.push(`status: ${payload.prevStatus ?? 'unknown'} -> ${payload.newStatus}`);
  if (payload.itemCount !== undefined) {
    lines.push(`itemCount: ${payload.itemCount}`);
  }
  if (payload.itemIds && payload.itemIds.length > 0) {
    lines.push(`itemIds: ${payload.itemIds.join(', ')}`);
  }
  if (payload.source) {
    lines.push(`source: ${payload.source}`);
  }
  if (payload.route) {
    lines.push(`route: ${payload.route}`);
  }
  lines.push(`timestamp: ${payload.timestamp}`);
  if (payload.env) {
    lines.push(`env: ${payload.env}`);
  }
  if (payload.appVersion) {
    lines.push(`appVersion: ${payload.appVersion}`);
  }
  if (payload.error) {
    lines.push(`error: ${payload.error}`);
  }
  return lines.join('\n');
};

export const sendStatusWriteFailureEmail = async (payload: StatusFailurePayload): Promise<boolean> => {
  const uid = payload.uid ?? 'anonymous';
  const dedupeKey = `${uid}|${payload.structureId}|${payload.suggestionId}|${payload.newStatus}`;
  if (isMailDedupeHit(dedupeKey)) return false;
  markMailDedupe(dedupeKey);

  const subject = truncate('Travelin | suggestions_status write failed', SUBJECT_MAX);
  const rawError = payload.error ? truncate(sanitizeText(payload.error), ERROR_MAX) : '';
  const textBody = truncate(buildTextBody({ ...payload, error: rawError }), TEXT_MAX);

  try {
    // NOTE: Firestore rules for /mail should only allow create when to == [RECIPIENT].
    await addDoc(collection(db, 'mail'), {
      to: [RECIPIENT],
      message: {
        subject,
        text: textBody,
      },
    });
    return true;
  } catch {
    return false;
  }
};
